import Image, { ImageProps } from "next/image"
import { useEffect, useState } from "react"

export enum ImageFallbacks {
  DEFAULT = "/icons/flextribe.png",
  // USER = "",
  // SUPPLIER = "",
}

export type ImageWithFallbackProps = (
  | {
      /**
       * The fallback image "src" to render.
       *
       * Should not be set if child element(s) are defined.
       */
      fallbackSrc: ImageFallbacks | string
      children?: undefined
    }
  | ({
      fallbackSrc?: undefined
    } & Required<Pick<ImageProps, "children">>)
) &
  ImageProps

const DEFAULT_FALLBACK_IMAGE = ImageFallbacks.DEFAULT

/**
 * Renders an image. If an error prevents the image from loading (e.g. 404 Not Found), a fallback will be shown instead.
 *
 * A `fallbackSrc` prop or child elements can be specified, but not both.
 *
 * @param src URL that points to an image.
 * @param fallbackSrc Fallback `img` `src` property to use in case of an error.
 * @param children Child elements to render in case of an error.
 */
export const ImageWithFallback = ({
  children,
  fallbackSrc = children ? undefined : DEFAULT_FALLBACK_IMAGE,
  src,
  onError,
  ...props
}: ImageWithFallbackProps) => {
  const [error, setError] = useState<React.SyntheticEvent<
    HTMLImageElement,
    Event
  > | null>(null)
  const [imgSrc, setImgSrc] = useState<typeof src>(src)

  useEffect(() => {
    setError(null)
    setImgSrc(src)
  }, [src])

  useEffect(() => {
    if (error && !children) setImgSrc(fallbackSrc)
  }, [fallbackSrc, error])

  function handleError(e: React.SyntheticEvent<HTMLImageElement, Event>) {
    setError(e)
    onError && onError(e)
  }

  return !(children && error) ? (
    <Image src={imgSrc} onError={handleError} {...props} />
  ) : (
    children
  )
}
